import { DeltaInsertOp } from '../renderer/DeltaInsertOp';
import { InsertOpsConverter } from '../renderer/InsertOpsConverter';
import { DataType } from '../renderer/value-types';
import { InlineValue } from './InlineValue';
import { EChartsValue } from './EChartsValue';
import { formatField } from './formatField';

export interface RichStyle {
  color?: string;
  fontStyle?: 'normal' | 'italic' | 'oblique';
  fontWeight?: string | number;
  fontFamily?: string;
  fontSize?: number;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  lineHeight?: number;
  backgroundColor?: string | any;
  borderColor?: string;
  borderWidth?: number;
  borderType?: string | number | Array<number>;
  borderDashOffset?: number;
  borderRadius?: number;
  padding?: number | Array<number>;
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  width?: number | string;
  height?: number | string;
  textBorderColor?: string;
  textBorderWidth?: number;
  textBorderType?: string | number | Array<number>;
  textBorderDashOffset?: number;
  textShadowColor?: string;
  textShadowBlur?: string;
  textShadowOffsetX?: number;
  textShadowOffsetY?: number;
}

export const convertDeltaToRich = (
  _quillDelta: string,
  _styleObj: any,
  _dataRow: any,
  _sizeFactor = 1,
  _content?: any,
  _customStyle: any = {},
): any => {
  const quill = JSON.parse(_quillDelta);
  const deltaOps: DeltaInsertOp[] = InsertOpsConverter.convert(quill.ops, {});

  // Transform ops attributes to formatter
  const rich: Record<string, RichStyle> = {};
  const formatter: string[] = [];
  const baseStyle = style2RichStyle(_styleObj, _sizeFactor);

  // rich.base = baseStyle;
  let idx = 0; // style counter

  for (const _op of deltaOps) {
    if (![DataType.EChartsValue as string, DataType.InlineValue as string, DataType.Text as string].includes(_op.insert.type)) continue;

    // Only alignment for the time being
    const _style = _customStyle.align ? {..._op.attributes, ..._customStyle} : _op.attributes

    rich[`style${idx}`] = {...baseStyle, ...style2RichStyle(_style, _sizeFactor)};
    const styleName = `style${idx}`;
    idx++;

    if (_op.insert.type === DataType.Text) {
      switch (_op.insert.value) {
        case '\n':
          formatter.push(`{${styleName}|\n}`);
          break;
        case '\t':
          formatter.push(`{${styleName}|\t}`);
          break;
        default:
          formatter.push(`{${styleName}|${_op.insert.value}}`);
          break;
      }
    } else if (_op.insert.type === DataType.InlineValue) {
      const inlineValue = InlineValue.create(_op);
      let inlineStr = inlineValue.resolveFields(_dataRow, '', _content);
      inlineStr = inlineStr.replace(/-/g, '&#8209;');
      formatter.push(`{${styleName}|${inlineStr}}`);
    } else if (_op.insert.type === DataType.EChartsValue) {
      const echartsValue = EChartsValue.create(_op);
      formatter.push(`{${styleName}|{${echartsValue.fieldName}}}`);
    } else {
      // No support for links, embeds, etc in Echarts
    }
  }

  return {
    formatter: formatter.join(''),
    rich
  };
};

// Transform the 23°-style object to EChart-style
const style2RichStyle = (_style: any, _sizeFactor = 1): RichStyle => {
  const style: any = { ..._style };

  if (style.background) {
    style.backgroundColor = _style.background;
    delete style.background;
  }

  if (style.font) {
    style.fontFamily = style.font;
    delete style.font;
  }

  if (style.size) {
    style.fontSize = Number.parseFloat(style.size) * _sizeFactor;
    delete style.size;
  }

  if (style.padding) {
    style.padding = [style.padding.top, style.padding.right, style.padding.bottom, style.padding.left];
  }

  if (style.position) {
    style.align = style.position;
    delete style.position;
  }

  if (style.weight) {
    style.fontWeight = style.weight;
    delete style.weight;
  }

  if (style.shadow) {
    style.textShadowColor = style.shadow.color;
    style.textShadowBlur = style.shadow.blur;
    style.textShadowOffsetX = style.shadow.x;
    style.textShadowOffsetY = style.shadow.y;
    delete style.shadow;
  }

  if (style.lineheight) {
    style.lineHeight = (style.lineheight.value === 'initial' ? 1 : style.lineheight.value) * style.fontSize;
    delete style.lineheight;
  }

  // Remove
  if (style.enabled) delete style.enabled;
  if (style.margin) delete style.margin;
  if (style.position) delete style.position;

  return style;
};
